import type { Contact } from './api';

/**
 * Mirror of the legacy `daysSince` helper from `kontakty_common.js`. Returns the
 * number of full days between today and the supplied date (or null when the
 * date is missing / unparseable).
 */
export function daysSince(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return null;
  const ms = Date.now() - ts;
  if (ms < 0) return 0;
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

/**
 * Pick the right
 * vehicle label based on the contact's `automobil` + `znacka_vozu`
 * discriminators.
 *
 * Brand `1` is **Ford**. Ford is the only brand whose model dropdown is
 * populated from `lms_vozy` (see `ContactRepository.getVehicleModels`),
 * so `nazev_vozu` (the LMS-joined name) carries the model for Ford; every
 * other brand stores the `vozidlo_modely.id` in `automobil` and the model
 * surfaces as `nazev_modelu`. The "Ford + automobil = 1" combination is
 * the legacy "Jiný" (other) sentinel and drops the brand label entirely
 * (it's redundant — we don't know the model).
 */
export function vehicleBrandLabel(c: Pick<Contact, 'automobil' | 'znacka_vozu' | 'nazev_znacky'>): string {
  if (c.automobil === 1 && c.znacka_vozu === 1) return '';
  return c.nazev_znacky ?? '';
}

export function vehicleModelLabel(
  c: Pick<Contact, 'znacka_vozu' | 'nazev_vozu' | 'nazev_modelu'>,
): string {
  if (c.znacka_vozu === 1) return c.nazev_vozu ?? '';
  return c.nazev_modelu ?? '';
}

export function contactDisplayName(c: Pick<Contact, 'jmeno_klienta' | 'spolecnost'>): string {
  return (c.jmeno_klienta?.trim() || c.spolecnost?.trim() || '—').slice(0, 80);
}

export function contactInitials(c: Pick<Contact, 'jmeno_klienta' | 'spolecnost'>): string {
  const source = c.jmeno_klienta?.trim() || c.spolecnost?.trim() || '';
  const parts = source.split(/\s+/).filter((p) => /^[A-Za-zÀ-ž]/.test(p));
  if (parts.length === 0) return '·';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export type PlanStav = 'aktivni' | 'splneny' | 'zruseny';

/**
 * Tone for the plan-status `Marker` — accent for aktivní (current work),
 * ok for splněný (done), neutral for zrušený (no-op).
 */
export function planTone(stav: PlanStav | string | null | undefined): 'accent' | 'ok' | 'neutral' {
  if (stav === 'splneny') return 'ok';
  if (stav === 'zruseny') return 'neutral';
  return 'accent';
}

export function planLabel(stav: PlanStav | string | null | undefined): string {
  if (stav === 'splneny') return 'Splněný';
  if (stav === 'zruseny') return 'Zrušený';
  return 'Aktivní';
}

const MESSAGE_DATE = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'short' });
const MESSAGE_DATE_WITH_YEAR = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

/**
 * Message-timeline date: `12. 4.` inside the current year, `12. 4. 2025`
 * for anything older (or in the future) so multi-year threads stay
 * unambiguous. Returns the raw input when it can't be parsed.
 */
export function formatMessageDate(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.getFullYear() === now.getFullYear()
    ? MESSAGE_DATE.format(d)
    : MESSAGE_DATE_WITH_YEAR.format(d);
}
